import * as React from "react";
import Select from 'react-select';
import {none, option, Option} from "ts-option";
import {PlaceholderProps} from "../../../../../common/props/PlaceholderProps";
import InvoiceSummary from "../../../../../../model/invoice/InvoiceSummary";
import {SelectComponentDefaultValue} from "../../../../../ui/select/SelectComponent";
import {ActionMeta, InputActionMeta} from "react-select/lib/types";
import {api} from "../../../../../../api/api";
import {notifyBugsnagAndShowToast} from "../../../../../../utils/Utils";
import DateUtils from "../../../../../../utils/DateUtils";
import CurrencyHelper from "../../../../../../helper/CurrencyHelper";
import classNames = require("classnames");

interface ComponentProps {
	/**
	 * Callback on select invoice.
	 * @param {Option<InvoiceSummary>} invoice.
	 */
	onSelect: (invoice: InvoiceSummary) => void;
	/**
	 * Initial search query text.
	 */
	initialSearchQuery?: Option<string>
}

type Props = ComponentProps & PlaceholderProps;

interface State {
	/**
	 * Text typed by user.
	 */
	queryText: string;
	/**
	 * Invoices available for user selection.
	 */
	invoices: InvoiceSummary[];
	/**
	 * Indicates if data is loading.
	 */
	loading: boolean;
	/**
	 * Last request query.
	 */
	lastRequestedQuery: Option<string>;
}

const minCharsToSearch = 2;

export default class InvoiceSelectionComponent extends React.PureComponent<Props, State> {
	public static defaultProps: Partial<Props> = {
		initialSearchQuery: none
	};

	constructor(props: Props) {
		super(props);
		this.onInputChange = this.onInputChange.bind(this);
		this.onSelected = this.onSelected.bind(this);
		this.getNoResultsText = this.getNoResultsText.bind(this);
		const initialQueryText = this.props.initialSearchQuery ? this.props.initialSearchQuery.getOrElse("") : "";
		this.state = {
			queryText: initialQueryText,
			invoices: [],
			loading: false,
			lastRequestedQuery: none
		};
	}

	render() {
		const value = this.state.queryText.length ?
			new SelectComponentDefaultValue(this.state.queryText, this.state.queryText) : null;
		return (
			<Select
				classNamePrefix={"c-select-component"}
				inputValue={this.state.queryText}
				value={value}
				styles={{menuList: (base) => ({...base, maxHeight: "186px"})}}
				placeholder={this.props.placeholder}
				noOptionsMessage={this.getNoResultsText}
				filterOption={(element: any, filter: string) => true}
				components={{Option: this.renderOptionComponent}}
				options={this.state.invoices}
				isLoading={this.state.loading}
				onInputChange={this.onInputChange}
				onChange={this.onSelected}
				closeMenuOnSelect={false}
				openMenuOnFocus={true}/>
		)
	}

	/**
	 * Render option component (select dropdown list element)
	 * @param props properties for list element.
	 */
	renderOptionComponent(props: any): JSX.Element {
		const {innerProps, innerRef, isFocused, data, className} = props;
		const classFromProps = classNames(
			{
				"c-reconciliation-modal_select-option-component": true,
				"has-focus": isFocused
			},
			className
		);

		return (
			<div ref={innerRef} className={classFromProps} {...innerProps}>
				<div className="c-reconciliation-modal_select-option-component_upper-container">
					<div className="c-reconciliation-modal_select-option-component_upper-container_labels">
						<div>
							<span>
								<b className="c-reconciliation-modal_select-option-component_upper-container_labels_blue-color">
									{data.invoiceNo.getOrElse("")}</b> <b>{data.referenceNumber}</b>
							</span>
							<span>{data.customerDisplayName}</span>
						</div>
						<div
							className="c-reconciliation-modal_select-option-component_upper-container_labels_blue-color fit-content">
							<span>{data.invoiceDate.format(DateUtils.DAILY_PLAN_DATE_FORMAT)}</span>
						</div>
					</div>
				</div>
				<div className="c-reconciliation-modal_select-option-component_border"/>
				<div className="c-reconciliation-modal_select-option-component_lower-container">
					<div>
						<span>Amount due</span>
						<span>Total</span>
					</div>
					<div className="c-reconciliation-modal_select-option-component_lower-container_values-container">
						<span>{CurrencyHelper.toCurrency(data.due)}</span>
						<span>{CurrencyHelper.toCurrency(data.total)}</span>
					</div>
				</div>
			</div>
		)
	}

	getNoResultsText(): string {
		if (this.state.loading)
			return "Loading ...";
		else if (this.state.queryText.length >= minCharsToSearch)
			return "No results found";
		else
			return "Type to search";
	}

	/**
	 * On changed value in select, fetch data from store or fetch from server.
	 * @param searchText text in the input.
	 * @param {InputActionMeta} inputActionMeta
	 */
	onInputChange(searchText: string, inputActionMeta: InputActionMeta) {
		if (inputActionMeta.action === 'input-change') {
			this.setState({queryText: searchText});
			if (searchText.length >= minCharsToSearch) {
				this.findBySearchText(searchText);
			}
			else {
				this.setState({invoices: [], loading: false});
			}
		}
	}

	/**
	 * Callback on selected item.
	 * @param {InvoiceSummary} selected selected item.
	 * @param {ActionMeta} actionMeta performed action data.
	 */
	onSelected(selected: InvoiceSummary, actionMeta: ActionMeta) {
		if (actionMeta.action === 'clear') {
			this.setState({queryText: "", invoices: []});
		}
		else if (!(selected instanceof Array)) {
			this.props.onSelect(selected);
		}
	}

	/**
	 * Reset search input text.
	 */
	public resetSearchInput() {
		this.setState({queryText: ""});
	}

	/**
	 * Fetch invoices by search text.
	 * @param {string} searchText text to search by.
	 */
	private findBySearchText(searchText: string) {
		this.setState({loading: true, lastRequestedQuery: option(searchText)}, () => {
			api.instance.invoiceApi.findInvoicesByText(searchText).then(result => {
				this.setState({invoices: result, loading: false});
			}).catch((error) => {
				this.setState({invoices: [], loading: false});
				notifyBugsnagAndShowToast(error, "Error on searching invoices");
			});
		});
	}
}
